import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Send as SendIcon,
  TrendingUp as TrendingUpIcon,
  Favorite as FavoriteIcon,
} from '@mui/icons-material';
import { useAuth } from '../../hooks';
import { useCompanyId } from '../../hooks/useCompanyId';
import { CompanyLayout } from '../../components/Layout';
import axios from 'axios';

interface StatCardProps {
  title: string;
  value: number | string;
  unit: string;
  icon: React.ReactNode;
  color: 'primary' | 'success' | 'info' | 'warning';
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  unit,
  icon,
  color,
}) => {
  const colorMap = {
    primary: '#2E5BBA',
    success: '#059669',
    info: '#0EA5E9',
    warning: '#D97706',
  };

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 48,
              height: 48,
              borderRadius: '12px',
              backgroundColor: `${colorMap[color]}20`,
              color: colorMap[color],
              mr: 2,
            }}
          >
            {icon}
          </Box>
          <Typography variant="h6" color="text.secondary">
            {title}
          </Typography>
        </Box>
        <Typography variant="h3" sx={{ color: '#111827', fontWeight: 700 }}>
          {typeof value === 'number' ? value.toLocaleString() : value}
          <Typography component="span" variant="h5" color="text.secondary">
            {typeof value === 'number' ? unit : ''}
          </Typography>
        </Typography>
      </CardContent>
    </Card>
  );
};

interface CompanyStats {
  total_likes: number;
  total_posts: number;
  total_views: number;
  published_posts: number;
}

interface CompanyDashboardStats {
  employeeCount: number;
  scoutsSentThisMonth: number;
  scoutReplyRate: number;
  totalLikes: number;
}

export const CompanyDashboardPage: React.FC = () => {
  const { user } = useAuth();
  const { companyId, loading: companyIdLoading } = useCompanyId();
  const [stats, setStats] = useState<CompanyStats | null>(null);
  const [dashboardStats, setDashboardStats] =
    useState<CompanyDashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [activities, setActivities] = useState<any[]>([]);
  const [activitiesLoading, setActivitiesLoading] = useState(true);

  // ダッシュボード統計を取得
  const fetchDashboardStats = useCallback(async () => {
    try {
      if (!companyId) {
        console.error('企業IDが見つかりません');
        return;
      }

      let employeeCount = 0;
      let scoutsSentThisMonth = 0;
      let scoutReplyRate = 0;

      // 従業員数を取得（最小限のデータで効率的に）
      try {
        const employeeResponse = await axios.get(
          `/api/companies/${companyId}/employees?page=1&limit=1`
        );
        if (employeeResponse.data.success) {
          employeeCount = employeeResponse.data.data.total || 0;
        }
      } catch (error) {
        console.warn('従業員数の取得に失敗:', error);
      }

      // スカウト履歴から統計を計算（stats APIが存在しないため）
      try {
        const scoutHistoryResponse = await axios.get(
          `/api/scouts/${companyId}/history?page=1&limit=100`
        );
        if (scoutHistoryResponse.data.success) {
          const messages = scoutHistoryResponse.data.data.messages || [];

          // 今月のスカウト数を計算
          const now = new Date();
          const thisMonth = now.getMonth();
          const thisYear = now.getFullYear();

          scoutsSentThisMonth = messages.filter((message: any) => {
            if (!message.sentAt) return false;
            const sentDate = new Date(message.sentAt);
            return (
              sentDate.getMonth() === thisMonth &&
              sentDate.getFullYear() === thisYear
            );
          }).length;

          // 返信率を計算（簡易版）
          const repliedMessages = messages.filter(
            (message: any) => message.status === 'replied'
          ).length;
          scoutReplyRate =
            messages.length > 0
              ? Math.round((repliedMessages / messages.length) * 100)
              : 0;
        }
      } catch (error) {
        console.warn('スカウト統計の取得に失敗:', error);
      }

      setDashboardStats({
        employeeCount,
        scoutsSentThisMonth,
        scoutReplyRate,
        totalLikes: stats?.total_likes || 0,
      });
    } catch (error) {
      console.error('ダッシュボード統計の取得に失敗:', error);
      // エラー時はデフォルト値を設定
      setDashboardStats({
        employeeCount: 0,
        scoutsSentThisMonth: 0,
        scoutReplyRate: 0,
        totalLikes: 0,
      });
    }
  }, [companyId, stats?.total_likes]);

  // 企業統計を取得
  useEffect(() => {
    const fetchStats = async () => {
      try {
        if (!companyId) {
          console.error('企業IDが見つかりません');
          setLoading(false);
          return;
        }

        const response = await axios.get(
          `/api/timeline/companies/${companyId}/stats`
        );
        if (response.data.success) {
          setStats(response.data.data);
        }
      } catch (error) {
        console.error('統計データの取得に失敗:', error);
        // エラー時はデフォルト値を設定
        setStats({
          total_likes: 0,
          total_posts: 0,
          total_views: 0,
          published_posts: 0,
        });
      } finally {
        setLoading(false);
      }
    };

    if (companyId && !companyIdLoading) {
      fetchStats();
      fetchActivities();
    }
  }, [companyId, companyIdLoading]);

  // statsが更新されたらdashboardStatsも更新
  useEffect(() => {
    if (stats && companyId) {
      fetchDashboardStats();
    }
  }, [stats, fetchDashboardStats, companyId]);

  // 最近の活動を取得
  const fetchActivities = async () => {
    try {
      setActivitiesLoading(true);
      if (!companyId) {
        console.error('企業IDが見つかりません');
        setActivitiesLoading(false);
        return;
      }

      const allActivities: any[] = [];

      // 1. スカウト履歴を取得
      try {
        const scoutResponse = await axios.get(
          `/api/scouts/${companyId}/history?page=1&limit=3`
        );
        if (scoutResponse.data.success) {
          const recentScouts = scoutResponse.data.data.messages.map(
            (scout: any) => {
              const sentDate = scout.sentAt
                ? new Date(scout.sentAt)
                : new Date();
              const isValidDate = !isNaN(sentDate.getTime());

              return {
                id: `scout_${scout.id}`,
                type: 'scout_sent',
                title: `${scout.recipientName || '不明'}さんにスカウトメッセージを送信`,
                timestamp: isValidDate ? sentDate : new Date(),
                displayTime: isValidDate
                  ? sentDate.toLocaleString('ja-JP', {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  : '日時不明',
                icon: '📧',
              };
            }
          );
          allActivities.push(...recentScouts);
        }
      } catch (error) {
        console.warn('スカウト履歴の取得に失敗:', error);
      }

      // 2. 従業員一覧を取得（最近追加された従業員）
      try {
        const employeeResponse = await axios.get(
          `/api/companies/${companyId}/employees?page=1&limit=3&sortBy=created_at&sortOrder=desc`
        );
        if (employeeResponse.data.success) {
          const recentEmployees = employeeResponse.data.data.employees.map(
            (emp: any) => {
              const createdDate = emp.createdAt
                ? new Date(emp.createdAt)
                : new Date();
              const isValidDate = !isNaN(createdDate.getTime());

              return {
                id: `employee_${emp.id}`,
                type: 'employee_added',
                title: `新しい従業員「${emp.user?.fullName || '不明'}」を追加`,
                timestamp: isValidDate ? createdDate : new Date(),
                displayTime: isValidDate
                  ? createdDate.toLocaleString('ja-JP', {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  : '日時不明',
                icon: '👤',
              };
            }
          );
          allActivities.push(...recentEmployees);
        }
      } catch (error) {
        console.warn('従業員履歴の取得に失敗:', error);
      }

      // 3. タイムライン統計を取得（MVP版：統計のみ、個別投稿履歴は取得しない）
      try {
        const timelineResponse = await axios.get(
          `/api/timeline/companies/${companyId}/stats`
        );
        if (timelineResponse.data.success) {
          const stats = timelineResponse.data.data;
          if (stats.total_posts > 0) {
            allActivities.push({
              id: 'timeline_stats',
              type: 'timeline_post',
              title: `タイムライン投稿: ${stats.total_posts}件、いいね: ${stats.total_likes}件`,
              timestamp: new Date(),
              displayTime: '現在の統計',
              icon: '📊',
            });
          }
        }
      } catch (error) {
        console.warn('タイムライン統計の取得に失敗:', error);
      }

      // 時系列でソートして最新10件を表示
      const sortedActivities = allActivities
        .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
        .slice(0, 8);

      if (sortedActivities.length === 0) {
        setActivities([
          {
            id: 'no_activity',
            type: 'info',
            title: '最近の活動はありません',
            displayTime: '---',
            icon: '📋',
          },
        ]);
      } else {
        setActivities(sortedActivities);
      }
    } catch (error) {
      console.error('活動履歴の取得に失敗:', error);
      setActivities([
        {
          id: 'error',
          type: 'error',
          title: 'データの取得に失敗しました',
          displayTime: '---',
          icon: '⚠️',
        },
      ]);
    } finally {
      setActivitiesLoading(false);
    }
  };

  // 統計データの表示用
  const displayStats = {
    employeeCount: dashboardStats?.employeeCount || 0,
    scoutsSentThisMonth: dashboardStats?.scoutsSentThisMonth || 0,
    scoutReplyRate: dashboardStats?.scoutReplyRate || 0,
    totalLikes:
      loading || companyIdLoading
        ? '読み込み中...'
        : dashboardStats?.totalLikes || 0,
  };

  // 企業IDの読み込み中は全体をローディング表示
  if (companyIdLoading) {
    return (
      <CompanyLayout>
        <Container maxWidth="xl">
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '50vh',
            }}
          >
            <CircularProgress size={40} />
          </Box>
        </Container>
      </CompanyLayout>
    );
  }

  // 企業IDが取得できない場合はエラー表示
  if (!companyId) {
    return (
      <CompanyLayout>
        <Container maxWidth="xl">
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '50vh',
            }}
          >
            <Typography variant="h6" color="error">
              企業情報を取得できませんでした。管理者にお問い合わせください。
            </Typography>
          </Box>
        </Container>
      </CompanyLayout>
    );
  }

  return (
    <CompanyLayout>
      <Container maxWidth="xl">
        {/* ヘッダー */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h4"
            sx={{
              mb: 1,
              color: '#111827',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <DashboardIcon sx={{ mr: 1 }} />
            企業ダッシュボード
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body1" color="text.secondary">
              ようこそ、{user?.email || '企業ユーザー'}さん
            </Typography>
            <Chip
              label={
                user?.userRole === 'company_admin' 
                  ? '管理者' 
                  : user?.userRole === 'hr' 
                  ? '人事' 
                  : '従業員'
              }
              size="small"
              color={
                user?.userRole === 'company_admin' 
                  ? 'primary' 
                  : user?.userRole === 'hr' 
                  ? 'secondary' 
                  : 'default'
              }
            />
          </Box>
        </Box>

        {/* 概要統計 */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="従業員数"
              value={displayStats.employeeCount}
              unit="名"
              icon={<PeopleIcon />}
              color="primary"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="今月のスカウト"
              value={displayStats.scoutsSentThisMonth}
              unit="件"
              icon={<SendIcon />}
              color="success"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="スカウト返信率"
              value={displayStats.scoutReplyRate}
              unit="%"
              icon={<TrendingUpIcon />}
              color="info"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="投稿いいね数"
              value={displayStats.totalLikes}
              unit="個"
              icon={<FavoriteIcon />}
              color="warning"
            />
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          {/* 最近の活動 */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 3, color: '#111827' }}>
                📋 最近の活動
              </Typography>
              <Box sx={{ space: 2 }}>
                {activitiesLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  activities.map((activity) => (
                    <Box
                      key={activity.id}
                      sx={{
                        p: 2,
                        border: '1px solid #E5E7EB',
                        borderRadius: 2,
                        mb: 2,
                        '&:last-child': { mb: 0 },
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.5,
                      }}
                    >
                      <Typography sx={{ fontSize: '1.2rem', mt: 0.2 }}>
                        {activity.icon}
                      </Typography>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 500, mb: 0.5 }}
                        >
                          {activity.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {activity.displayTime}
                        </Typography>
                      </Box>
                    </Box>
                  ))
                )}
              </Box>
            </Paper>
          </Grid>

          {/* クイックアクション */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 3, color: '#111827' }}>
                🎯 クイックアクション
              </Typography>
              <Box sx={{ space: 2 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  主要な機能への直接アクセス
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body2">
                    • 学生検索・スカウト作成
                  </Typography>
                  <Typography variant="body2">• 従業員管理</Typography>
                  <Typography variant="body2">
                    • 企業プロフィール編集
                  </Typography>
                  <Typography variant="body2">
                    • タイムライン投稿管理
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </CompanyLayout>
  );
};
